import React from 'react';
import { Helmet } from 'react-helmet';
import '../ManagedServices/ManagedCDN.css';

const CONTACT_URL = 'https://meetings.hubspot.com/viacheslav-seifer';

const DevSecOpsOfferPage = ({
    scope,
    icon,
    summary,
    oneTimePrice,
    recurringPrice,
    purchaseUrl,
    oneTimeDescription,
    recurringDescription,
}) => {
    const options = [
        {
            type: 'One-time',
            price: oneTimePrice,
            suffix: 'one-time',
            description: oneTimeDescription,
            cta: `Purchase ${scope} One-time`,
            href: purchaseUrl,
            primary: true,
        },
        {
            type: 'Recurring',
            price: recurringPrice,
            suffix: 'per month',
            description: recurringDescription,
            cta: `Talk to Us About ${scope} Recurring`,
            href: CONTACT_URL,
        },
    ];

    return (
        <>
            <Helmet>
                <title>{`DevSecOps ONE — ${scope} DevSecOps Support | Epicfinal`}</title>
                <meta
                    name="description"
                    content={`Professional ${scope} DevSecOps support on demand, available as a one-time engagement or a recurring service.`}
                />
                <html lang="en" />
            </Helmet>

            <div className="managed-cdn-page">
                <section className="cdn-hero">
                    <div className="container">
                        <div className="cdn-hero-content">
                            <div className="cdn-icon-badge"><i className={icon}></i></div>
                            <h1 className="cdn-hero-title">DevSecOps ONE — {scope}</h1>
                            <p className="cdn-hero-subtitle">A DevSecOps engineer on demand, without a full-time hire.</p>
                            <p className="cdn-hero-description">{summary}</p>
                            <div className="cdn-hero-buttons">
                                <a href="#plans" className="btn-cdn btn-primary">Choose Your Option</a>
                                <a href={CONTACT_URL} className="btn-cdn btn-secondary" target="_blank" rel="noopener noreferrer">
                                    Talk to Us
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="devsecops-plans" id="plans">
                    <div className="container">
                        <div className="cdn-section-header">
                            <h2>Choose the Engagement That Fits</h2>
                            <p>Start with a focused one-time engagement or choose recurring support for ongoing needs.</p>
                        </div>
                        <div className="devsecops-scope-grid devsecops-scope-grid--single">
                            <article className="devsecops-scope-card devsecops-scope-card--ltr">
                                <div className="devsecops-scope-header">
                                    <span>DevSecOps ONE</span>
                                    <h3>{scope}</h3>
                                    <p>{scope === 'Lite' ? 'Focused support for targeted work and ongoing needs at a lighter scope.' : 'More delivery capacity and continuity for projects and ongoing technical work.'}</p>
                                </div>
                                <div className="devsecops-options-grid">
                                    {options.map((option) => (
                                        <div className="devsecops-plan-option" key={option.type}>
                                            <span className={`devsecops-option-type ${option.primary ? '' : 'devsecops-option-type--recurring'}`}>
                                                {option.type}
                                            </span>
                                            <div className="devsecops-option-price">
                                                <strong>{option.price}</strong>
                                                <span>{option.suffix}</span>
                                            </div>
                                            <p>{option.description}</p>
                                            <a
                                                href={option.href}
                                                className={`devsecops-plan-cta ${option.primary ? 'devsecops-plan-cta--primary' : ''}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {option.cta}
                                                <i className="las la-arrow-right" aria-hidden="true"></i>
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            </article>
                        </div>

                        <div className="devsecops-monthly-promo">
                            <div>
                                <span>Need broader monthly coverage?</span>
                                <p>Explore DevSecOps ONE Monthly for continuous, full-scope support.</p>
                            </div>
                            <div className="devsecops-monthly-promo-offer">
                                <strong>$5,400 <small>per month</small></strong>
                                <a href={CONTACT_URL} className="devsecops-monthly-promo-link" target="_blank" rel="noopener noreferrer">
                                    Discuss Monthly
                                    <i className="las la-arrow-right" aria-hidden="true"></i>
                                </a>
                            </div>
                        </div>
                        <p className="devsecops-plans-note">
                            Recurring service renews monthly. The one-time option creates no ongoing commitment.
                        </p>
                    </div>
                </section>

                <section className="cdn-features">
                    <div className="container">
                        <div className="cdn-section-header">
                            <h2>Professional Support, Guided by Your Priorities</h2>
                            <p>Structured access to DevSecOps expertise at the service level you choose.</p>
                        </div>
                        <div className="cdn-features-grid">
                            <div className="cdn-feature-card">
                                <div className="cdn-feature-icon"><i className="las la-tasks"></i></div>
                                <h3>Requests as Needed</h3>
                                <p>Send us tasks and requests based on your business needs and priorities.</p>
                            </div>
                            <div className="cdn-feature-card">
                                <div className="cdn-feature-icon"><i className="las la-code"></i></div>
                                <h3>Custom Development</h3>
                                <p>Software, integrations, automation, and tools tailored to your environment.</p>
                            </div>
                            <div className="cdn-feature-card">
                                <div className="cdn-feature-icon"><i className="las la-project-diagram"></i></div>
                                <h3>Architecture Consulting</h3>
                                <p>Solution design across infrastructure, cloud, performance, and scalability.</p>
                            </div>
                            <div className="cdn-feature-card">
                                <div className="cdn-feature-icon"><i className="las la-user-check"></i></div>
                                <h3>Context That Carries Forward</h3>
                                <p>Recurring service builds ongoing familiarity with your systems and business needs.</p>
                            </div>
                            <div className="cdn-feature-card">
                                <div className="cdn-feature-icon"><i className="las la-infinity"></i></div>
                                <h3>End-to-End DevSecOps</h3>
                                <p>Development, infrastructure, automation, monitoring, and security in one service.</p>
                            </div>
                            <div className="cdn-feature-card">
                                <div className="cdn-feature-icon"><i className="las la-shield-alt"></i></div>
                                <h3>Security Built In</h3>
                                <p>Security principles are integrated into planning and delivery from the start.</p>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="cdn-included">
                    <div className="container">
                        <div className="cdn-section-header">
                            <h2>What We Can Do for You</h2>
                            <p>DevSecOps expertise across three core areas.</p>
                        </div>
                        <div className="cdn-included-grid">
                            <div className="cdn-included-column">
                                <h3><i className="las la-check-circle"></i> Development</h3>
                                <ul>
                                    <li><i className="las la-angle-right"></i> Custom software development</li>
                                    <li><i className="las la-angle-right"></i> API integrations</li>
                                    <li><i className="las la-angle-right"></i> Scripts and automation</li>
                                    <li><i className="las la-angle-right"></i> CI/CD pipelines</li>
                                </ul>
                            </div>
                            <div className="cdn-included-column">
                                <h3><i className="las la-check-circle"></i> Infrastructure</h3>
                                <ul>
                                    <li><i className="las la-angle-right"></i> Cloud architecture</li>
                                    <li><i className="las la-angle-right"></i> Infrastructure as Code</li>
                                    <li><i className="las la-angle-right"></i> Containers and orchestration</li>
                                    <li><i className="las la-angle-right"></i> Performance and availability</li>
                                </ul>
                            </div>
                            <div className="cdn-included-column">
                                <h3><i className="las la-check-circle"></i> Security &amp; Operations</h3>
                                <ul>
                                    <li><i className="las la-angle-right"></i> Security reviews</li>
                                    <li><i className="las la-angle-right"></i> System hardening</li>
                                    <li><i className="las la-angle-right"></i> Monitoring and alerts</li>
                                    <li><i className="las la-angle-right"></i> Incident and issue response</li>
                                    <li><i className="las la-angle-right"></i> Operational improvements</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="cdn-cta">
                    <div className="container">
                        <div className="cdn-cta-content">
                            <h2>Not Sure Which Option Fits?</h2>
                            <p>Tell us what you need, and we will help you choose the right engagement.</p>
                            <div className="cdn-cta-buttons">
                                <a href={CONTACT_URL} className="btn-cdn btn-light" target="_blank" rel="noopener noreferrer">
                                    Schedule a Consultation
                                </a>
                                <a href="#plans" className="btn-cdn btn-outline">Review the Options</a>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

export default DevSecOpsOfferPage;
